/**
 * Smoothly scrolls the page to an element indicated by its ID.
 *
 * @param {string} elementId - The ID of the element to scroll into view.
 * @param {ScrollIntoViewOptions} [options={}] - Optional scrolling options to override default behavior.
 */
export const scrollToElementById = (elementId: string, options: ScrollIntoViewOptions = {}) => {
  const element = document.getElementById(elementId);

  if (!element) {
    return;
  }

  element.scrollIntoView({ behavior: 'smooth', block: 'end', ...options });
};

/** Returns the text from the start of a contentEditable up to the caret. */
export const getTextBeforeCaret = (el: HTMLElement): string => {
  const sel = window.getSelection();
  const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
  if (!range || !el.contains(range.endContainer)) {
    return el.innerText;
  }
  const pre = range.cloneRange();
  pre.selectNodeContents(el);
  pre.setEnd(range.endContainer, range.endOffset);
  return pre.toString();
};

/** Places the cursor at the end of a contentEditable element. */
export const setCursorAtEnd = (el: HTMLElement) => {
  el.focus();
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
};

/** Places the cursor at a character offset within a contentEditable, clamping past the end. */
export const setCursorAtOffset = (el: HTMLElement, offset: number) => {
  el.focus();
  const sel = window.getSelection();
  if (!sel) {
    return;
  }
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const length = node.textContent?.length ?? 0;
    if (remaining <= length) {
      const range = document.createRange();
      range.setStart(node, remaining);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    remaining -= length;
  }
  setCursorAtEnd(el);
};

/** Downloads the file at a URL under the given filename. */
export const downloadFromUrl = (url: string, filename: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  // Firefox has historically needed the anchor in the document for the click to fire.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const TOP_OFFSET_PX = 16;

export const isScrollableY = (el: HTMLElement): boolean => {
  const { overflowY } = getComputedStyle(el);
  return (overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight;
};

const findScrollParent = (el: HTMLElement): HTMLElement | null => {
  let node = el.parentElement;
  while (node && !isScrollableY(node)) {
    node = node.parentElement;
  }
  return node;
};

type ScrollIntoScrollParentOptions = { block?: 'start' | 'nearest' };

export const scrollIntoScrollParent = (
  el: HTMLElement,
  { block = 'start' }: ScrollIntoScrollParentOptions = {}
) => {
  const parent = findScrollParent(el);
  if (!parent) {
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    return;
  }
  const box = parent.getBoundingClientRect();
  const target = el.getBoundingClientRect();
  const offsetTop = target.top - box.top;

  if (block === 'start') {
    parent.scrollTo({ top: parent.scrollTop + offsetTop - TOP_OFFSET_PX, behavior: 'smooth' });
    return;
  }
  if (offsetTop < 0) {
    parent.scrollTo({ top: parent.scrollTop + offsetTop, behavior: 'smooth' });
    return;
  }
  const overflowBottom = target.bottom - box.bottom;
  if (overflowBottom > 0) {
    parent.scrollTo({ top: parent.scrollTop + overflowBottom, behavior: 'smooth' });
  }
};

export const scheduleScrollIntoScrollParent = (
  getElement: () => HTMLElement | null | undefined,
  options?: ScrollIntoScrollParentOptions
): (() => void) => {
  let frame = requestAnimationFrame(() => {
    frame = requestAnimationFrame(() => {
      const el = getElement();
      if (el) {
        scrollIntoScrollParent(el, options);
      }
    });
  });
  return () => cancelAnimationFrame(frame);
};
